<template>
  <div ref="container"
       class="passenger details-container details-container--thin"
       :class="{ 'details-containerTs': data.brandNameFormatted === Brand.Travelsphere }">
    <h3 class="details-container__header"
        :class="{ 'details-container__headerTs': data.brandNameFormatted === Brand.Travelsphere }">{{ details.firstName }} {{ details.lastName }}</h3>

    <div class="details-container__body"
         v-show="isEditable">
      <div v-show="passenger.summaryView">
        <button class="details-container__edit-button"
                :class="{ 'details-container__edit-buttonTs': data.brandNameFormatted === Brand.Travelsphere }"
                @click="edit"
                aria-label="Edit your details" 
                title="Click to edit your details">Edit</button>

        <PassengerSummary v-if="passenger.summaryView"
                          :details="details" />
      </div>

      <div v-show="!passenger.summaryView">
        <div class="details-container__body-intro">
          <p>Enter details as they appear on the passport</p>
        </div>

        <Input name="title"
               label="Title"
               placeholder="Please select"
               :required="true"
               :options="titleOptions"
               :vuexPath="vuexPath" 
               :value="title"
               @change="selectTitleOptions"
               ref="title"
               @input.native="saveToSessionStorage('title', $event.target.value)" />

        <Input name="firstName"
               label="First Name"
               placeholder="First Name"
               :required="true"
               :pattern="patterns.name"
               :title="titles.name"
               :trim="true"
               :vuexPath="vuexPath"
               ref="firstName"
               @input.native="saveToSessionStorage('firstName', $event.target.value)" />

        <Input name="otherNames"
               label="Other Names"
               placeholder="Other Names"
               :pattern="patterns.name"
               :title="titles.name"
               :trim="true"
               :vuexPath="vuexPath"
               ref="otherNames"
               @input.native="saveToSessionStorage('otherNames', $event.target.value)" />

        <Input name="lastName"
               label="Surname"
               placeholder="Surname"
               :required="true"
               :pattern="patterns.name"
               :title="titles.name"
               :trim="true"
               :vuexPath="vuexPath"
               ref="lastName"
               @input.native="saveToSessionStorage('lastName', $event.target.value)" />

        <template v-if="passenger.lead">
          <Input name="email"
                 type="email"
                 label="Email"
                 placeholder="Email"
                 :required="true"
                 :vuexPath="vuexPath"
                 :pattern="patterns.email"
                 :title="titles.email"
                 :trim="true"
                 ref="email"
                 @input.native="saveToSessionStorage('email', $event.target.value)" />

          <Input name="confirmEmail"
                 type="email"
                 label="Confirm Email"
                 placeholder="Confirm Email"
                 :required="true"
                 :customValidation="matchEmail"
                 :vuexPath="vuexPath"
                 :pattern="patterns.email"
                 :title="titles.email"
                 :trim="true"
                 ref="confirmEmail"
                 @input.native="saveToSessionStorage('confirmEmail', $event.target.value)" />

          <Input name="mobilePhone"
                 type="tel"
                 label="Mobile Phone Number"
                 placeholder="Mobile Phone Number"
                 :required="true"
                 :pattern="patterns.telephone"
                 :title="titles.telephone"
                 :trim="true"
                 :vuexPath="vuexPath"
                 ref="mobilePhone"
                 @input.native="saveToSessionStorage('mobilePhone', $event.target.value)" />

          <Input name="homePhone"
                 type="tel"
                 label="Home Phone Number"
                 placeholder="Home Phone Number"
                 :required="true"
                 :pattern="patterns.telephone"
                 :title="titles.telephone"
                 :trim="true"
                 :vuexPath="vuexPath"
                 ref="homePhone"
                 @input.native="saveToSessionStorage('homePhone', $event.target.value)" />
        </template>

        <Date v-if="passenger.lead"
              ref="date"
              :validateAge="18"
              :vuexPath="vuexPath"
              :under18ErrorMessage="data.passengersError.under18ErrorMessage"
              @change.native="saveDateToSessionStorage" />
        <Date v-else
              ref="date"
              :vuexPath="vuexPath"
              :validateAge="18"
              :under18ErrorMessage="data.passengersError.under18ErrorMessage"
              @change.native="saveDateToSessionStorage" />

        <Input name="sex"
               label="Sex (indicated by passport)"
               placeholder="Please select"
               :required="true"
               :options="['Male', 'Female']"
               :vuexPath="vuexPath"
               ref="sex"
               @input.native="saveToSessionStorage('sex', $event.target.value)" />

        <AddressLookup v-if="passenger.lead"
                      ref="address"
                      title="Billing Address"
                      :vuexPath="vuexAddressPath"
                      :brandName="data.brandNameFormatted"
                      @address-updated="saveAddressToSessionStorage"
                      @input.native="saveAddressToSessionStorage" />

        <Visas v-if="visas.length"
               :visas="visas"
               :description="data.passengerDetails.visaDescription"
               :vuexPath="vuexVisaPath"
               :currency="currency"
               @input.native="saveToSessionStorage('visa', $event.target.value)" />

         <Input name="mobilityLimitations"
               label="Does this traveller have any medical conditions or mobility limitations?"
               :required="true"
               :options="[true, false]"
               :optionsRadio="true"
               :startingValue="false"
               :vuexPath="vuexPath" 
               ref="mobilityLimitations"
               @change.native="saveToSessionStorage('mobilityLimitations', $event.target.value)"/>

       <div v-if="details.mobilityLimitations"
             class="mobility-text">
          <Input name="mobilityDetails"
                 label="Mobility Details"
                 placeholder="Mobility details..."
                 :required="details.mobilityLimitations"
                 :vuexPath="vuexPath"
                 ref="mobilityDetails"
                 @input.native="saveToSessionStorage('mobilityDetails', $event.target.value)" />
        </div>

        <div v-if="details.mobilityLimitations"
             class="error-message">
          <div class="error-message__icon"></div>
          <div class="error-message__text"
               v-html="data.passengerDetails.medicalConditionWarning"></div>
        </div>

        <Input name="dietaryRequests"
               label="Does this traveller have any special dietary requests or allergies?"
               :description="data.passengerDetails.dietaryDescription"
               :required="true"
               :options="[true, false]"
               :optionsRadio="true"
               :startingValue="false"
               :vuexPath="vuexPath"
               ref="dietaryRequests"
               @change.native="saveToSessionStorage('dietaryRequests', $event.target.value)" />

       <div v-if="details.dietaryRequests"
             class="dietary-text">
          <Input name="dietaryRequirements"
                 label="Dietary Requirements"
                 placeholder="Dietary requirements..."
                 :required="details.dietaryRequests"
                 :vuexPath="vuexPath"
                 ref="dietaryRequirements"
                 @input.native="saveToSessionStorage('dietaryRequirements', $event.target.value)" />
        </div>

        <template v-if="passenger.lead">
          <Input name="termsAndConditions"
                 type="checkbox"
                 :label="termsAndConditionsLabel"
                 :required="true"
                 :vuexPath="vuexPath"
                 ref="termsAndConditions"
                 @input.native="saveToSessionStorage('termsAndConditions', $event.target.checked)" />

          <Input name="marketingMail"
                 type="checkbox"
                 :startingValue="true"
                 :label="marketingMailLabel()"
                 :vuexPath="vuexPath" 
                 ref="marketingMail"
                 @input.native="saveToSessionStorage('marketingMail', $event.target.checked)" />

          <Input name="marketingEmail"
                 type="checkbox"
                 :startingValue="true"
                 :label="marketingEmailLabel()"
                 :vuexPath="vuexPath" 
                 ref="marketingEmail"
                 @input.native="saveToSessionStorage('marketingEmail', $event.target.checked)" />
        </template>

        <div class="details-container__footer">
          <button class="btn details-container__submit"
                  @click="save"
                  aria-label="Save and continue" 
                  title="Click to save and continue"
                  >Save and continue</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Input from '@/vue/components/Form/Input.vue';
import Date from '@/vue/components/Form/Date.vue';
import AddressLookup from '@/vue/components/Form/AddressLookup.vue';
import { scrollToElement } from '@/utils';
import PassengerSummary from './PassengerSummary.vue';
import Visas from './Visas.vue';
import { matchField } from '@/validation';
import ValidationMixin from '@/vue/components/Form/validationMixin';
import {Brand} from './../../../../brand.js';

export default {
  name: 'Passenger',
  mixins: [ValidationMixin],

  components: {
    Input,
    Date,
    AddressLookup,
    PassengerSummary,
    Visas
  },

  data() {
    return {
      Brand,
      titleOptions: [],
      title: ''
    };
  },

  props: ['passenger', 'index'],

  computed: {
    ...mapState('checkout', ['visas', 'passengers', 'data', 'quote']),
    ...mapGetters('checkout', ['currency', 'passengersDetails']),
    ...mapGetters(['formIsValid']),

    vuexPath() {
      return ['checkout', 'passengers', this.index];
    },

    vuexAddressPath() {
      return [...this.vuexPath, 'address'];
    },

    vuexVisaPath() {
      return [...this.vuexPath, 'visaIds'];
    },

    details() {
      return this.passengersDetails[this.index];
    },

    isEditable() {
      return this.$store.getters['checkout/passengerIsEditable'](
        this.passenger
      );
    },

    termsAndConditionsLabel() {
      if (this.data.passengerDetails.termsAndConditionsLink) {
        return `I agree to the <a target="_blank" href="${this.data.passengerDetails.termsAndConditionsLink.url}">${this.data.passengerDetails.termsAndConditionsLink.name}</a> set out by ${this.data.brandNameFormatted}`;
      }
      return `I agree to the Terms and Conditions set out by ${this.data.brandNameFormatted}`;
    },
  },

  watch: {
    'details.email'() {
      this.$refs.confirmEmail.setFieldError();
    },

    'passenger.summaryView'(summaryView) {
      if (summaryView) {
        scrollToElement(this.$refs.container, true);
      }
    },

    'details.mobilityLimitations'(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          const sessionData = JSON.parse(sessionStorage.getItem('passengerDetails')) || {};
          const passengerDetails = sessionData[this.index];
          
          if (passengerDetails && passengerDetails.mobilityDetails) {
            this.updateField('mobilityDetails', passengerDetails.mobilityDetails);
          }
        });
      }
    },

    'details.dietaryRequests'(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          const sessionData = JSON.parse(sessionStorage.getItem('passengerDetails')) || {};
          const passengerDetails = sessionData[this.index];
          
          if (passengerDetails && passengerDetails.dietaryRequirements) {
            this.updateField('dietaryRequirements', passengerDetails.dietaryRequirements);
          }
        });
      }
    }
  },

  mounted() {
    this.$nextTick().then(this.setInitialFormData);
  },

  methods: {
    setInitialFormData() {
      const passenger = { ...this.passenger };
      const details = { ...this.details };
      if (passenger.email) {
        passenger.confirmEmail = passenger.email;
      }
      if (!details.title) {
        this.titleOptions = ['Mr', 'Mrs', 'Miss', 'Ms'];
      } else {
        this.titleOptions = [];
      }
      if (!passenger.title) {
        this.titleOptions = ['Mr', 'Mrs', 'Miss', 'Ms'];
      } else {
        this.titleOptions = [];
      }
      this.$store.commit('setFieldValuesFromFlat', {
        flat: passenger,
        vuexPath: this.vuexPath
      });

      this.populateFields();
    },

    label(label) {
      return label + this.passenger.passengerId;
    },

    save() {
      // set forms as submitted to show error states if necessary
      this.$store.commit('setHasBeenSubmitted', this.vuexPath);

      // validate visas if they exist
      if (this.visas.length) {
        this.$store.commit('setHasBeenSubmitted', this.vuexVisaPath);
      }

      if (this.formIsValid(this.vuexPath)) {
        this.$store.commit('setSummaryView', this.vuexPath);

        // if the last passenger send all passengers details to api
        if (this.index === this.passengers.length - 1) {
          this.$store.dispatch('checkout/passengers');

          var dataLayerItem = JSON.parse(JSON.stringify({
            currency: this.quote.currency,
            value: this.quote.subtotal,
            quote: this.quote,
            brand: this.data.brand,
            data: this.$store.state.checkout
          }));

          GTM.addShippingInfo(dataLayerItem);
          GTM.newsletterSubscribe(this.passengers[0]);
        }
      }
    },

    edit() {
      this.$store.commit('unsetSummaryView', this.vuexPath);

      // reset payment incase passenger details change
      this.$store.commit('checkout/resetPayment');
    },

    matchEmail(confirmEmail) {
      return matchField(confirmEmail, this.$refs.email.$refs.input);
    },

    marketingMailLabel() {
      return `I want to receive marketing communication from ${this.data.brandNameFormatted} via mail`
    },

    marketingEmailLabel() {
      return `I want to receive marketing communication from ${this.data.brandNameFormatted} via email`
    },

    selectTitleOptions() {
      this.titleOptions = ['Mr', 'Mrs', 'Miss', 'Ms'];
    },

    saveAddressToSessionStorage() {
      const sessionData = JSON.parse(sessionStorage.getItem('passengerDetails')) || {};

      if (!sessionData[this.index]) {
        sessionData[this.index] = {};
      }

      const addressData = this.$refs.address.address;
      sessionData[this.index].address = addressData;

      sessionStorage.setItem('passengerDetails', JSON.stringify(sessionData));
    },

    saveDateToSessionStorage() {
      this.$nextTick(() => {
        const dayOfBirth = this.$refs.date.values.dayOfBirth;
        const monthOfBirth = this.$refs.date.values.monthOfBirth;
        const yearOfBirth = this.$refs.date.values.yearOfBirth;

        if (dayOfBirth && monthOfBirth && yearOfBirth) {
          this.saveToSessionStorage('dayOfBirth', dayOfBirth);
          this.saveToSessionStorage('monthOfBirth', monthOfBirth);
          this.saveToSessionStorage('yearOfBirth', yearOfBirth);
        }
      });
    },

    saveToSessionStorage(field, value) {
      const isBooleanField = ['dietaryRequests', 'mobilityLimitations'].includes(field);
      const boolValue = isBooleanField && value === 'true';

      const sessionData = JSON.parse(sessionStorage.getItem('passengerDetails')) || {};
      const indexData = sessionData[this.index] || {};

      if (isBooleanField && boolValue === false) {
        delete indexData[field];
      } else {
        indexData[field] = isBooleanField ? boolValue : value;
      }

      // Check if there are existing values in sessionStorage for mobilityDetails and dietaryRequirements
      if (field === 'mobilityLimitations' && boolValue) {
        if (sessionData[this.index].mobilityDetails !== undefined) {
          indexData.mobilityDetails = sessionData[this.index].mobilityDetails;
        }
      }

      if (field === 'dietaryRequests' && boolValue) {
        if (sessionData[this.index].dietaryRequirements !== undefined) {
          indexData.dietaryRequirements = sessionData[this.index].dietaryRequirements;
        }
      }

      sessionData[this.index] = indexData;
      sessionStorage.setItem('passengerDetails', JSON.stringify(sessionData));
    },

    populateFields() {
      const sessionData = JSON.parse(sessionStorage.getItem('passengerDetails')) || {};
      const passengerDetails = sessionData[this.index];

      if (passengerDetails) {
        Object.keys(passengerDetails).forEach((fieldName) => {
          if (fieldName !== 'address' && fieldName !== 'mobilityDetails' && fieldName !== 'dietaryRequirements') {
            this.updateField(fieldName, passengerDetails[fieldName]);
          }
        });

        if (passengerDetails.address) {
          this.$refs.address.address = { ...passengerDetails.address };
        }

      }
    },

    updateField(fieldName, value) {
      if (this.passenger.fields[fieldName]) {
        this.passenger.fields[fieldName] = {
          ...this.passenger.fields[fieldName],
          value,
          userHasTyped: false,
          error: false,
        };
      }
    }
  
  }
};
</script>
